// frontend/src/components/library/BookCard.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tag, User, Edit, Globe } from 'lucide-react';
import { MEDIA_BASE_URL } from '../../config'; 

const DetailItem = ({ icon: Icon, text }) => {
    if (!text || (typeof text === 'object' && !text.name && !text.title)) return null;
    const displayText = (typeof text === 'object') ? text.name || text.title : text;
    return (
        <div className="flex items-center text-[11px] text-thin text-gray-500">
            <Icon size={14} className="ml-1.5 text-gray-400" />
            <span className="truncate">{displayText}</span>
        </div>
    );
};

const getFinalCoverUrl = (baseUrl, relativeUrl) => {
    if (!relativeUrl) return null;
    if (relativeUrl.startsWith('http') || relativeUrl.startsWith('https')) {
        return relativeUrl;
    }
    const base = baseUrl.replace(/\/$/, '');
    const relative = relativeUrl.startsWith('/') ? relativeUrl.substring(1) : relativeUrl;
    return `${base}/${relative}`;
};

const BookCard = ({ book }) => {
    const navigate = useNavigate();
    if (!book) return null;
    
    const { 
        slug, 
        title, 
        cover_image_url, 
        author, 
        translator, 
        publisher, 
        tags 
    } = book;
    
    const finalCoverUrl = cover_image_url 
        ? getFinalCoverUrl(MEDIA_BASE_URL, cover_image_url) 
        : `https://placehold.co/400x600/e2e8f0/94a3b8?text=${encodeURIComponent(title)}`;

    const handleCardClick = () => {
        navigate(`/books/${slug}`);
    };

    return (
        <div
            onClick={handleCardClick}
            className="
                book-card flex w-full h-[180px] 
                bg-white rounded-xl border border-gray-100 
                overflow-hidden transition-shadow duration-200 hover:shadow-xl
                group cursor-pointer
            "
        >
            {/* بخش تصویر - عرض متغیر، ارتفاع کامل */}
            <div className="flex-shrink-0 h-full">
                <img 
                    src={finalCoverUrl} 
                    alt={title} 
                    className="h-full w-auto object-contain" 
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/400x600/e2e8f0/94a3b8?text=${encodeURIComponent(title)}`;
                    }}
                />
            </div>
            
            {/* بخش محتوا - باقیمانده فضا */}
            <div className="flex-1 flex flex-col justify-between p-2 rtl text-right overflow-hidden">
                <div>
                    <h3 
                        className="text-[12px] font-bold text-gray-800 mb-2 line-clamp-3" 
                        title={title}
                    >
                        {title}
                    </h3>
                    <div className="space-y-1.5">
                        <DetailItem icon={User} text={author} />
                        <DetailItem icon={Edit} text={translator} />
                        <DetailItem icon={Globe} text={publisher} />
                    </div>
                </div>

                {tags && tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 border-t border-gray-200 pt-1 overflow-hidden max-h-12">
                        {tags.slice(0, 3).map((tag) => (
                            <Link
                                key={tag.slug}
                                to={`/books?tags__slug=${tag.slug}`}
                                onClick={(e) => e.stopPropagation()}
                                className="
                                    inline-flex items-center px-2 py-0.5 
                                    text-[9px] font-medium text-gray-700 bg-gray-100 
                                    rounded-full hover:bg-gray-200 transition-colors
                                "
                            >
                                <Tag size={12} className="ml-1" />
                                {tag.name || tag.title}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookCard;